/**
 * App Component
 */
import {
  Component,
  HostListener,
  OnInit,
  Renderer2,
  ViewEncapsulation,
  AfterViewInit,
  ViewChild,
  ViewContainerRef,
  ElementRef,
  ApplicationRef,
  OnDestroy,
  PlatformRef,
} from '@angular/core';
import {
  NavigationEnd,
  ResolveStart,
  RouteConfigLoadEnd,
  RouteConfigLoadStart,
  Router,
} from '@angular/router';
import {
  DevToolsExtension,
  NgRedux,
  select,
  select$,
} from '@angular-redux/store';
import { NgReduxRouter } from '@angular-redux/router';
import { compact, get, head, split, isFunction } from 'lodash';

import { Observable, rx } from './shared/rx';
import { IAppState, MiddlewaresProvider, rootReducer } from './store';
import {
  XprtDataActions,
  RouterActions,
  SessionActions,
  ToolsActions,
  UiActions,
} from '@store/actions';
import { XUXDrawerSidebarService, IMenuItem } from '@app/xenial-ux';
import {
  EntityServiceProvider,
  PostMessageService,
  STATE_SELECTORS,
  StateService,
  SidebarService,
  WindowWrapperService,
  LocalStorageService,
  ToolsService,
} from '@shared/services';
import { INITIAL_STATE, IProgressBarLoader } from '@store/reducers/ui';
import { ACCESS_TOKEN } from '@config/index';
import { ComponentCounterService } from '@shared/helpers/component-counter';
import { environment } from '../environments/environment';
import { DropdownMenuService } from '@geniuspos/ui-shared/modules/xdm-shared-service/dropdown-menu/dropdown-menu.service';
import { ToolbarButtonDefinition } from '@geniuspos/ui-shared';
import { setLocale } from '@shared/utils/date-utils';
import { I18n } from './i18n/strings-keys';

/**
 * App Component
 * Top Level Component
 */
// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xvi-app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.style.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('dropDownMenuContainer', { read: ViewContainerRef })
  public dropDownMenuContainer: ViewContainerRef;
  @select$(['ui', 'showNavbar'], (o$) =>
    o$.pipe(
      rx.map((v) => !v),
      rx.delay(0),
    ),
  )
  public navbarHidden$: Observable<boolean>;
  @select(['userSession', 'user']) public user$: Observable<any>;
  @select(['userSession', 'errors']) public userSessionErrors$: Observable<any>;
  @select(['userSession', 'isIframeMode'])
  public isIframeMode$: Observable<boolean>;
  @select(['userSession', 'applicationStarted'])
  public isAppStarted$: Observable<boolean>;
  @select$(['ui', 'loader'], (o$) =>
    o$.pipe(rx.debounceTime(0), rx.distinctUntilChanged()),
  )
  public loader$: Observable<boolean>;
  @select(['ui', 'menuItems']) public menuItems$: Observable<IMenuItem[]>;
  @select(['ui', 'hiddenMenu']) public hiddenMenu$: Observable<boolean>;
  @select(['ui', 'selectedMenuItem'])
  public selectedMenuItem$: Observable<IMenuItem>;
  @select(['ui', 'theme']) public theme$: Observable<string>;
  @select$(['ui', 'progressBarLoader'], (loader$) =>
    loader$.pipe(rx.debounceTime(0), rx.distinctUntilChanged()),
  )
  public progressBarLoader$: Observable<IProgressBarLoader>;
  @select(['ui', 'breadcrumb', 'buttons'])
  public breadcrumbButtons$: Observable<ToolbarButtonDefinition[]>;
  @select(['preferences', 'company', 'regional_settings', 'locale'])
  public locale$: Observable<string>;

  public readonly I18n = I18n;

  private portalMenuVisibility: boolean = false;

  private isAppStarted: boolean = false;

  /**
   * Initialize the component
   * Components need initialization in the constructor.
   */
  // eslint-disable-next-line max-params
  public constructor(
    private ngRedux: NgRedux<IAppState>,
    private ngReduxRouter: NgReduxRouter,
    public devTool: DevToolsExtension,
    public middlewaresProvider: MiddlewaresProvider,
    private uiActions: UiActions,
    private toolsActions: ToolsActions,
    private router: Router,
    private sidebarService: SidebarService,
    private drawerSidebarService: XUXDrawerSidebarService,
    private renderer: Renderer2,
    private routerActions: RouterActions,
    private postMessageService: PostMessageService,
    private stateService: StateService,
    public entityServiceProvider: EntityServiceProvider,
    private sessionActions: SessionActions,
    private componentCounterService: ComponentCounterService,
    private windowWrapperService: WindowWrapperService,
    public dropDownMenuService: DropdownMenuService,
    private localStorageService: LocalStorageService,
    private toolsService: ToolsService,
    private el: ElementRef,
    private appRef: ApplicationRef,
    private platform: PlatformRef,
  ) {
    const epicMiddleware = middlewaresProvider.getEpicMiddleware();

    this.ngRedux.configureStore(
      rootReducer,
      this.getInitState(),
      [epicMiddleware, ...middlewaresProvider.getMiddlewares()],
      [devTool.isEnabled() ? devTool.enhancer() : (f): any => f],
    );

    epicMiddleware.run(middlewaresProvider.getRootEpic());

    if (!environment.production) {
      this.componentCounterService.injectInWindow();
    }
  }

  /**
   * This function is called right after the directive's
   * data-bound properties have been checked for the first time,
   * and before any of its children have been checked.
   * It is invoked only once when the directive is instantiated.
   * In this function we listen to route state. And once current url
   * isn't /login then we check if customer is logged in. And if
   * customer isn't logged in then we do navigate to login page.
   * Also in this function we select needed sidebar menu item
   * to update title of the sidebar link
   */
  public ngOnInit(): void {
    this.isAppStarted$.subscribe((isAppStarted) => {
      this.handleAppState(isAppStarted);
    });

    this.router.events.subscribe((event) => {
      if (event instanceof RouteConfigLoadStart) {
        this.uiActions.enableLoader();
      } else if (event instanceof RouteConfigLoadEnd) {
        this.uiActions.disableLoader();
      }
      if (event instanceof ResolveStart) {
        // clear breadcrumb buttons first
        this.uiActions.updateBreadcrumbButtons([]);
      }

      if (event instanceof NavigationEnd) {
        const dom = (<HTMLElement>this.el.nativeElement)?.querySelector(
          'dm-row-options-renderer',
        );
        dom?.parentNode.removeChild(dom);
        const uiRoute = head(compact(split(event.url, '/')));
        const service = this.entityServiceProvider.getServiceClass([
          'config.uiRoute',
          uiRoute,
        ]);
        const entityType = get(service, 'config.ENTITY_TYPE', null);
        const permissionEntityType = get(
          service,
          'config.entityNameToCheckPermissions',
          entityType,
        );
        // There are cases where the entityType and entityType in permissions can be different.
        // For example, in UI we can have inventory-count-group entity, and inventory-count is used to check permissions
        const suppressPermissionEntityTypeUsageConfig = get(
          service,
          'config.suppressPermissionEntityTypeUsageForNavigation',
          false,
        );

        const suppressPermissionEntityTypeUsage = isFunction(
          suppressPermissionEntityTypeUsageConfig,
        )
          ? suppressPermissionEntityTypeUsageConfig(event)
          : suppressPermissionEntityTypeUsageConfig;

        const entityTypeForNavigation = suppressPermissionEntityTypeUsage
          ? entityType
          : permissionEntityType;
        // we think permission entity type is the page data entity type
        this.routerActions.navigationEnd(event.url, entityTypeForNavigation);
      }
    });

    this.ngReduxRouter.initialize();

    this.toolsActions.startUp();
    this.toolsActions.fetchComponentInfo();
    this.sessionActions.setIframeMode(this.windowWrapperService.isInIframe());

    const isExternalLogin = this.toolsService.isExternalLogin();

    // TODO fix app navigating if user delete token from localStore
    if (!this.localStorageService.getItem(ACCESS_TOKEN) && !isExternalLogin) {
      void this.router.navigateByUrl('login');
    }

    this.userSessionErrors$.subscribe((errors) => {
      if (!isExternalLogin && get(errors, '0.meta.code') === 'INVALID_TOKEN') {
        void this.router.navigateByUrl('login');
      }
    });

    this.user$.subscribe((user) => {
      if (user) {
        this.drawerSidebarService.openOnDesktop();
      }
    });

    this.theme$
      .pipe(rx.distinctUntilChanged(), rx.pairwise())
      .subscribe(([oldTheme, newTheme]) => {
        if (oldTheme) {
          oldTheme
            .split(' ')
            .forEach((themeClass) =>
              this.renderer.removeClass(document.body, themeClass),
            );
        }
        if (newTheme) {
          newTheme
            .split(' ')
            .forEach((themeClass) =>
              this.renderer.addClass(document.body, themeClass),
            );
        }
      });

    this.locale$.subscribe((locale: string) => {
      setLocale(locale);
    });
  }

  public ngAfterViewInit(): void {
    this.dropDownMenuService.setContainer(this.dropDownMenuContainer);
  }

  public ngOnDestroy(): void {
    /**
     * clear all app ref components
     */
    this.appRef.components?.forEach((component) => {
      this.appRef.detachView(component.hostView);
      component.destroy();
    });
  }

  /**
   * This function creates initial state
   *
   * @returns
   */
  public getInitState(): IAppState {
    const initialUiState = Object.assign({}, INITIAL_STATE, {
      menuItems: this.sidebarService.createItems(),
    });

    return {
      ui: initialUiState,
    };
  }

  /**
   * Disables loader
   * TODO: Remove when loader is stable
   */
  public closeLoader(): void {
    this.uiActions.disableLoader();
  }

  /**
   * use this to catch browser refresh
   */
  @HostListener('window:beforeunload', ['$event'])
  public handleBeforeUnload() {
    this.platform.destroy();
  }

  /**
   * Add handler which emits a message to portal to prevent user logout due to inactivity
   * on each document click
   * It is also used to hide any open popup menus.
   */
  @HostListener('document:click')
  public dispatchClickToParentIframe(): void {
    const iframeMode = this.stateService.getBySelector(
      STATE_SELECTORS.IFRAME_MODE,
    );

    if (iframeMode) {
      this.postMessageService.sendMessage(XprtDataActions.USER_ACTIVITY_EVENT);
    }

    if (this.portalMenuVisibility) {
      this.postMessageService.sendRawMessage('hidePortalSubMenu');
    }
  }

  @HostListener('window:message', ['$event'])
  public iframeMessageListener(event: MessageEvent): void {
    if (!event?.data) return;
    try {
      const val =
        typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      this.portalMenuVisibility = get(val, 'portalSubMenuVisibility', false);
      this.postMessageService.processPostMessage(val);
    } catch {}
  }

  /**
   * The method called when the app state is changed (login/logout)
   */
  private handleAppState(isAppStarted: boolean): void {
    this.isAppStarted = isAppStarted;
    if (this.isAppStarted) {
      this.onAppStarted();
    }
  }
  /**
   * The method called when the app is started
   */
  private onAppStarted(): void {}
}
